import ErrorIcon from '@mui/icons-material/Error';
import { FormControl, Grid, MenuItem, Select, SelectProps, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';
import { ReactComponent as ExpandArrowIcon } from '../../assets/icons/iconExpandArrow.svg';
import { Container } from './styles';
import { Input } from '../Inputs/Input';
import { slugify } from '../../utils/slugify';

type DropdownPropsItem = {
  id?: string | number;
  value?: string | number;
};

type Props = SelectProps & {
  errorText?: string;
  placeholderTitle?: string;
  itens?: DropdownPropsItem[];
  name: string;
  control: any;
  isSearchable?: boolean;
};

export function SelectControled({
  itens,
  errorText,
  name,
  placeholderTitle,
  control,
  isSearchable,
  ...props
}: Props) {
  const theme = useTheme();
  const {
    field: { ref, onChange },
  } = useController({
    name,
    control,
  });
  const [itensFiltered, setItensFiltered] = useState<DropdownPropsItem[] | undefined>(itens);
  const [isOpen, setIsOpen] = useState(false);
  const [defaultValues] = useState({
    search: '',
  });

  const {
    control: controlSearch,
    watch,
    setValue,
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });
  const search = watch('search');

  const MenuProps = {
    PaperProps: {
      style: {
        backgroundColor: '#F1F1F1',
        color: theme.colors.secondary,
        maxHeight: 250,
      },
    },
    disableScrollLock: true,
  };

  const handleOpen = () => {
    setIsOpen(true);
    document.body.classList.add('no-scroll');
  };

  const handleClose = () => {
    setIsOpen(false);
    setValue('search', '');
    document.body.classList.remove('no-scroll');
  };

  const handleSearchClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (isOpen) {
      event.stopPropagation();
    }
  };

  useEffect(() => {
    if (!itens) return;
    if (!search) {
      setItensFiltered(itens);
      return;
    }
    setItensFiltered(
      itens.filter((item) => {
        if (item.value) {
          return slugify(item.value.toString()).includes(slugify(search));
        }
        return false;
      }),
    );
  }, [itens, search]);
  return (
    <Container>
      <FormControl fullWidth>
        <Select
          open={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          displayEmpty
          classes={{ select: 'select-style' }}
          ref={ref}
          onChange={onChange}
          MenuProps={MenuProps}
          onKeyDown={(e) => e.stopPropagation()}
          IconComponent={ExpandArrowIcon}
          {...props}>
          {isSearchable && (
            <Grid display="flex" alignItems="center" justifyContent="center" justifyItems="center">
              <Grid width="90%">
                <Input
                  control={controlSearch}
                  name="search"
                  placeholder="Pesquisar..."
                  onClick={handleSearchClick}
                />
              </Grid>
            </Grid>
          )}
          {itensFiltered?.length && (
            <MenuItem
              disabled
              value=""
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '2.5rem',
                fontSize: '1.1rem',
              }}>
              {placeholderTitle}
            </MenuItem>
          )}
          {itensFiltered && itensFiltered.length ? (
            itensFiltered?.map(({ id, value }) => (
              <MenuItem
                key={id + uuid()}
                value={id}
                style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
                onKeyDown={(e) => e.stopPropagation()}>
                <Tooltip disableInteractive title={value} arrow>
                  <input
                    value={value}
                    readOnly
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      width: '100%',
                      height: '1.5rem',
                      maxWidth: '80%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: theme.colors.secondary,
                      cursor: 'pointer',
                      outline: 'none',
                      fontSize: '1.1rem',
                      alignSelf: 'center',
                    }}
                  />
                </Tooltip>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled value="">
              Nenhum resultado encontrado
            </MenuItem>
          )}
        </Select>
      </FormControl>
      {errorText && (
        <span className="fieldError">
          <ErrorIcon fontSize="inherit" className="errorIcon" /> {errorText}
        </span>
      )}
    </Container>
  );
}
